import type { TwoFactorMethod } from '@chukua/common';
import { Alert, Button, Form, Input } from 'antd';
import { KeyRound, LifeBuoy, Mail, Smartphone } from 'lucide-react';
import { CodeInput } from '../../../shared/components/CodeInput';
import { DisplayedCodeNotice } from '../../../shared/components/DisplayedCodeNotice';
import type { LoginCodeStep } from '../hooks/use-login-code-step';

const INSTRUCTIONS: Record<TwoFactorMethod, string> = {
  totp: 'Enter the 6-digit code from your authenticator app.',
  email: 'We emailed you a 6-digit code. Enter it below.',
};

const SWITCH_LABELS: Record<TwoFactorMethod, { label: string; icon: typeof Mail }> = {
  totp: { label: 'Use my authenticator app instead', icon: Smartphone },
  email: { label: 'Email me a code instead', icon: Mail },
};

function instructions(step: LoginCodeStep): string {
  if (step.entry === 'recovery') return 'Enter one of the recovery codes you saved when you turned on two-factor.';
  if (step.method === 'email' && step.delivery === 'sending') return 'Sending a 6-digit code to your email…';
  if (step.method === 'email' && step.displayedCode) return 'Enter the 6-digit code shown below.';
  return INSTRUCTIONS[step.method];
}

function CodeField({ step }: { step: LoginCodeStep }) {
  if (step.entry === 'recovery') {
    return (
      <Form.Item name="code" label="Recovery code" rules={[{ required: true, message: 'Enter a recovery code' }]}>
        <Input prefix={<KeyRound className="size-4 text-muted" />} autoComplete="off" autoFocus placeholder="xxxx-xxxx-xxxx-xxxx" />
      </Form.Item>
    );
  }
  return (
    <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter the 6-digit code' }]}>
      <CodeInput autoFocus onComplete={(code) => void step.submit(code)} />
    </Form.Item>
  );
}

export function LoginCodeForm({ step }: { step: LoginCodeStep }) {
  const alternative = step.otherMethod && SWITCH_LABELS[step.otherMethod];

  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-fg">Two-factor authentication</h1>
      <p className="mt-1.5 mb-8 text-sm text-muted">{instructions(step)}</p>
      {step.method === 'email' && step.entry === 'code' && step.displayedCode && <DisplayedCodeNotice className="mb-5" code={step.displayedCode} />}
      {step.error && <Alert className="mb-5" type="error" title={step.error} showIcon />}
      <Form<{ code: string }>
        key={step.entry}
        layout="vertical"
        size="large"
        requiredMark={false}
        onFinish={(values) => step.submit(values.code)}
        disabled={step.submitting}
      >
        <CodeField step={step} />
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" block loading={step.submitting}>
            Verify
          </Button>
        </Form.Item>
      </Form>
      <div className="mt-6 flex flex-col items-start gap-1 text-sm">
        {step.method === 'email' && step.delivery === 'sent' && (
          <Button type="link" className="px-0" onClick={() => void step.resendEmailCode()}>
            Send another code
          </Button>
        )}
        <Button type="link" className="px-0" icon={<LifeBuoy className="size-4" />} onClick={step.toggleRecoveryCode}>
          {step.entry === 'code' ? 'Use a recovery code' : 'Use a 6-digit code'}
        </Button>
        {alternative && (
          <Button type="link" className="px-0" icon={<alternative.icon className="size-4" />} onClick={step.switchMethod}>
            {alternative.label}
          </Button>
        )}
      </div>
    </>
  );
}
